import { ISLAND_SHAPES, ISLAND_SHAPE_NAMES, getRandomIslandType } from './TerrainConstants.js';

/**
 * IslandTypeManager - Centralizes island type management
 * Maintains a single source of truth for the current island type
 */
class IslandTypeManager {
    constructor() {
        this.currentType = 0;
        this.listeners = [];
        
        // Initialize from window if already set
        if (window.selectedIslandType !== undefined) {
            this.currentType = window.selectedIslandType;
        }
    }
    
    /**
     * Get the current island type
     * @returns {number} The current island type (0-5)
     */
    getType() {
        return this.currentType;
    }
    
    /**
     * Get the name of the current island type
     * @returns {string} The name of the current island type
     */
    getTypeName() {
        return ISLAND_SHAPE_NAMES[this.currentType] || "Unknown Island";
    }
    
    /**
     * Set the current island type
     * @param {number} type - The island type to set (0-5)
     */
    setType(type) {
        if (type >= 0 && type <= 5) {
            this.currentType = type;
            window.selectedIslandType = type; // Update global for backward compatibility
            console.log(`[IslandTypeManager] Island type set to: ${type} (${this.getTypeName()})`);
            this.notifyListeners();
        } else {
            console.warn(`[IslandTypeManager] Invalid island type: ${type}`);
        }
    }
    
    /**
     * Add a listener for island type changes
     * @param {Function} callback - The callback to call when the island type changes
     */
    addListener(callback) {
        this.listeners.push(callback);
    }
    
    /**
     * Notify all listeners of the current island type
     */
    notifyListeners() {
        this.listeners.forEach(callback => {
            try {
                callback(this.currentType);
            } catch (error) {
                console.error('[IslandTypeManager] Error in listener:', error);
            }
        });
    }
    
    /**
     * Generate a random island type
     * @returns {number} The generated island type
     */
    generateRandomType() {
        const randomType = getRandomIslandType();
        this.setType(randomType);
        return randomType;
    }
    
    /**
     * Initialize from URL parameters or generate random
     * @param {boolean} forceRandom - Whether to force a random type regardless of URL
     * @returns {number} The initialized island type
     */
    initialize(forceRandom = false) {
        // Reset current type to default
        this.currentType = 0;
        window.selectedIslandType = undefined;
        
        const urlParams = new URLSearchParams(window.location.search);
        const typeParam = urlParams.get('islandType');
        const forceParam = urlParams.get('_t');
        
        if (!forceRandom && typeParam !== null) {
            // Use URL parameter if it exists
            const type = parseInt(typeParam);
            if (!isNaN(type) && type >= 0 && type <= 5) {
                this.setType(type);
                // If the _t parameter is present, this is a forced value
                const logMessage = forceParam 
                    ? `[IslandTypeManager] Initialized with forced type from URL: ${type} (${this.getTypeName()})`
                    : `[IslandTypeManager] Initialized from URL: ${type} (${this.getTypeName()})`;
                console.log(logMessage);
                return type;
            }
        }
        
        // Generate random type
        const randomType = this.generateRandomType();
        console.log(`[IslandTypeManager] Initialized with random type: ${randomType} (${this.getTypeName()})`);
        return randomType;
    }
    
    /**
     * Sync the current island type to the URL
     */
    syncToURL() {
        const url = new URL(window.location);
        url.searchParams.set('islandType', this.currentType);
        window.history.replaceState({}, '', url.toString());
    }
    
    // Singleton instance
    static instance = null;
    
    /**
     * Get the singleton instance
     * @returns {IslandTypeManager} The singleton instance
     */
    static getInstance() {
        if (!IslandTypeManager.instance) {
            IslandTypeManager.instance = new IslandTypeManager();
        }
        return IslandTypeManager.instance;
    }
}

export default IslandTypeManager; 